import React, { useState, useEffect } from 'react';

const ServerList = ({ sortable = false }) => {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    const fetchServers = async () => {
      try {
        const response = await fetch('/api/servers');
        if (!response.ok) {
          throw new Error('Failed to fetch servers');
        }
        const data = await response.json();
        setServers(data.servers || []);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchServers();
    const interval = setInterval(fetchServers, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleSort = (field) => {
    if (!sortable) return;
    
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIndicator = ({ field }) => {
    if (!sortable || sortField !== field) return null;
    return (
      <span style={{ marginLeft: '4px', color: '#FFFFFF' }}>
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  const getDisplayServers = () => {
    if (!sortable || !sortField) return servers;
    
    return [...servers].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === 'war3_version') {
        aValue = a.war3_version;
        bValue = b.war3_version;
      } else if (sortField === 'players') {
        aValue = parseInt(a.players);
        bValue = parseInt(b.players);
      }

      if (aValue === bValue) return 0;
      
      const comparison = aValue > bValue ? 1 : -1;
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const handleCopy = (text, serverId) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(serverId);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px', color: '#CFCFCF' }}>
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '20px', color: '#FF2000' }}>
        Error: {error}
      </div>
    );
  }

  if (servers.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '20px', color: '#CFCFCF' }}>
        No servers found
      </div>
    );
  }

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    fontFamily: 'Tahoma, Arial, Verdana, Helvetica, sans-serif',
    fontSize: '10px',
    color: '#CFCFCF'
  };

  const thStyle = {
    padding: '2px 5px',
    background: '#3C3C3C',
    color: '#FFFFFF',
    fontWeight: 'bold',
    textAlign: 'left',
    cursor: sortable ? 'pointer' : 'default'
  };

  const tdStyle = {
    padding: '2px 5px',
    background: '#181818',
    color: '#FFFFFF'
  };

  const tdAltStyle = {
    ...tdStyle,
    background: '#101010'
  };

  const buttonStyle = {
    background: '#3C3C3C',
    border: 'none',
    color: '#FFFFFF',
    padding: '2px 10px',
    cursor: 'pointer',
    fontSize: '10px',
    fontFamily: 'Tahoma, Arial, Verdana, Helvetica, sans-serif'
  };

  return (
    <table style={tableStyle}>
      <thead>
        <tr>
          <th style={thStyle} onClick={() => handleSort('server_name')}>
            Server Name
            <SortIndicator field="server_name" />
          </th>
          <th style={thStyle} onClick={() => handleSort('app_id')}>
            Game
            <SortIndicator field="app_id" />
          </th>
          <th style={thStyle} onClick={() => handleSort('map')}>
            Map
            <SortIndicator field="map" />
          </th>
          <th style={thStyle} onClick={() => handleSort('players')}>
            Players
            <SortIndicator field="players" />
          </th>
          <th style={thStyle} onClick={() => handleSort('war3_version')}>
            Version
            <SortIndicator field="war3_version" />
          </th>
          <th style={thStyle} onClick={() => handleSort('country_code')}>
            Country
            <SortIndicator field="country_code" />
          </th>
          <th style={thStyle} onClick={() => handleSort('status')}>
            Status
            <SortIndicator field="status" />
          </th>
          <th style={thStyle}>Action</th>
        </tr>
      </thead>
      <tbody>
        {getDisplayServers().map((server, index) => (
          <tr key={index}>
            <td style={index % 2 === 0 ? tdStyle : tdAltStyle}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ marginRight: '8px' }}>
                  <i className="ri-server-line" style={{ color: '#888888' }}></i>
                </div>
                <div>
                  <div>{server.server_name}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: '#888888', fontSize: '9px' }}>{server.ip}:{server.port}</span>
                    <button
                      onClick={() => handleCopy(`${server.ip}:${server.port}`, index)}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#888888' }}
                      title="Copy server address"
                    >
                      {copiedId === index ? (
                        <i className="ri-check-line" style={{ fontSize: '9px', color: '#20C000' }}></i>
                      ) : (
                        <i className="ri-file-copy-line" style={{ fontSize: '9px' }}></i>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </td>
            <td style={index % 2 === 0 ? tdStyle : tdAltStyle}>
              <img 
                src={`/app_images/app_id_${server.app_id}.png`} 
                alt="Game Icon" 
                style={{ width: '16px', height: '16px' }}
                title={server.app_id === 10 ? "Counter-Strike 1.6" : server.app_id === 80 ? "Counter-Strike: Condition Zero" : "Day of Defeat"}
              />
            </td>
            <td style={index % 2 === 0 ? tdStyle : tdAltStyle}>{server.map}</td>
            <td style={index % 2 === 0 ? tdStyle : tdAltStyle}>{server.players}</td>
            <td style={index % 2 === 0 ? tdStyle : tdAltStyle}>
              <div>
                <div>{server.war3_version}</div>
              </div>
            </td>
            <td style={index % 2 === 0 ? tdStyle : tdAltStyle}>{server.country_code || 'N/A'}</td>
            <td style={index % 2 === 0 ? tdStyle : tdAltStyle}>
              <span style={{ color: server.status.toLowerCase() !== 'online' ? '#FF2000' : '#20C000' }}>
                {server.status}
              </span>
            </td>
            <td style={index % 2 === 0 ? tdStyle : tdAltStyle}>
              <button 
                onClick={() => window.location.href = server.connect_url}
                style={buttonStyle}
              >
                Connect
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export { ServerList };
export default ServerList; 